import { forwardRef, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react'
import * as stylex from '@stylexjs/stylex'

const styles = stylex.create({
  field: { display: 'flex', flexDirection: 'column', rowGap: '8px' },
  label: { color: '#3d463f', fontSize: '13px', fontWeight: 600 },
  hint: { color: '#59665d', fontSize: '12px', lineHeight: '1.15rem' },
  message: { borderRadius: '0.75rem', padding: '10px 12px', fontSize: '12px', lineHeight: '1.15rem' },
  messageInfo: { backgroundColor: '#eef6ed', color: '#397a4b' },
  messageError: { backgroundColor: '#fff1ed', color: '#a34e29' },
  messageSuccess: { backgroundColor: '#edf7eb', color: '#397a4b' },
  control: {
    width: '100%',
    minHeight: '44px',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#87978a',
    borderRadius: '0.75rem',
    backgroundColor: '#fff',
    paddingInline: '12px',
    fontSize: '16px',
    color: '#27342b',
    outline: 'none',
    transition: 'border-color 150ms, box-shadow 150ms',
    '::placeholder': { color: '#657268', opacity: 1 },
    ':hover': { borderColor: '#5f7665' },
    ':focus': { borderColor: '#397a4b', boxShadow: '0 0 0 3px rgb(57 122 75 / 20%)' },
    ':disabled': { cursor: 'not-allowed', opacity: 0.6 },
  },
  textarea: { minHeight: '96px', paddingBlock: '10px', resize: 'vertical' },
})

type FormFieldProps = {
  children: ReactNode
  hint?: string
  label: string
}

export function FormField({ children, hint, label }: FormFieldProps) {
  return <label {...stylex.props(styles.field)}><span {...stylex.props(styles.label)}>{label}</span>{children}{hint && <span {...stylex.props(styles.hint)}>{hint}</span>}</label>
}

export const Field = FormField

type FormMessageProps = { children: ReactNode; tone?: 'error' | 'info' | 'success' }

export function FormMessage({ children, tone = 'info' }: FormMessageProps) {
  const toneStyle = tone === 'error' ? styles.messageError : tone === 'success' ? styles.messageSuccess : styles.messageInfo
  return <p {...stylex.props(styles.message, toneStyle)}>{children}</p>
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className = '', style, ...props }, ref) {
  const controlProps = stylex.props(styles.control)
  return <input {...props} className={`${controlProps.className} ${className}`.trim()} ref={ref} style={{ ...controlProps.style, ...style }} />
})

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ children, className = '', style, ...props }, ref) {
  const controlProps = stylex.props(styles.control)
  return <select {...props} className={`${controlProps.className} ${className}`.trim()} ref={ref} style={{ ...controlProps.style, ...style }}>{children}</select>
})

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className = '', style, ...props }, ref) {
  const controlProps = stylex.props(styles.control, styles.textarea)
  return <textarea {...props} className={`${controlProps.className} ${className}`.trim()} ref={ref} style={{ ...controlProps.style, ...style }} />
})
